// GlassLoginForm.jsx - CORRECTED VERSION
import React, { useState } from 'react';
import { Lock, Mail, Eye, EyeOff, TrendingUp, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ login: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [inputType, setInputType] = useState('');
  const navigate = useNavigate();

  // Create axios instance WITHOUT Authorization header initially
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://api.cdaethi.org', // Use your live URL
    headers: { 
      'Content-Type': 'application/json', 
      'Accept': 'application/json' 
    },
    timeout: 30000
  });

  // Add interceptor to attach token AFTER login (for subsequent requests)
  api.interceptors.request.use(config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  }, error => Promise.reject(error));

  const handleChange = (e) => {
    const value = e.target.value;
    if (value.includes('@')) setInputType('email');
    else if (/^\d+$/.test(value)) setInputType('tin');
    else setInputType('');
    
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const isEmail = formData.login.includes('@');
      const requestData = isEmail 
        ? { email: formData.login, password: formData.password }
        : { tin: formData.login, password: formData.password };

      // NO Authorization header needed for login - token doesn't exist yet
      const response = await api.post('/login', requestData);

      if (response.data.success && response.data.token) {
        // Store the token from response
        const { token, user, role } = response.data;
        
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', role || user?.role);
        
        console.log('✅ Login successful! Token stored:', token.substring(0, 20) + '...');
        
        await Swal.fire({
          icon: 'success',
          title: 'Login Successful!',
          text: `Welcome back, ${user?.name || 'User'}!`,
          timer: 1500,
          showConfirmButton: false
        });

        redirectBasedOnRole(role || user?.role);
      } else {
        throw new Error('No token received from server');
      }
    } catch (error) {
      console.error('Login error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config
      });
      
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Invalid email/TIN or password';
      } else if (error.response?.status === 403) {
        errorMessage = 'Your account is not activated or has been blocked';
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Cannot connect to server. Please check if the API is running.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      await Swal.fire({
        icon: 'error',
        title: 'Login Failed',
        text: errorMessage,
        confirmButtonColor: '#4f46e5'
      });
    } finally {
      setLoading(false);
    }
  };

  const redirectBasedOnRole = (role) => {
    const roleLower = role?.toLowerCase();
    const routes = {
      'admin': '/admin/dashboard',
      'inspector': '/inspector/dashboard',
      'trader': '/trader/dashboard',
      'user': '/dashboard',
    };
    
    const redirectPath = routes[roleLower] || '/dashboard';
    console.log(`🔄 Redirecting to: ${redirectPath} (Role: ${role})`);
    navigate(redirectPath);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950">
      <div className="relative w-full max-w-lg px-4">
        <div className="backdrop-blur-xl bg-white/5 rounded-3xl border border-white/10 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>
          
          <div className="p-8 md:p-10">
            <div className="text-center mb-10">
              <div className="bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 p-5 rounded-3xl inline-flex mb-8">
                <div className="flex items-center space-x-1">
                  <BarChart3 className="h-8 w-8 text-white" />
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
              </div>
              <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-blue-300 via-indigo-200 to-purple-300 bg-clip-text text-transparent">
                ETIMS
              </h1>
              <p className="text-indigo-200/80 text-sm">Ethiopian Trade Inspection & Management System</p>
            </div>
           
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Mail className="h-5 w-5 text-blue-400" />
                  <label className="text-sm font-medium text-indigo-200">Email or TIN Number</label>
                </div>
                <input
                  type="text"
                  name="login"
                  value={formData.login}
                  onChange={handleChange}
                  className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-indigo-400"
                  placeholder={inputType === 'email' ? "Email address" : inputType === 'tin' ? "TIN number" : "Email or TIN"}
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Lock className="h-5 w-5 text-blue-400" />
                    <label className="text-sm font-medium text-indigo-200">Password</label>
                  </div>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-indigo-400 pr-14"
                    placeholder="••••••••"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-300"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white font-semibold py-4 rounded-2xl disabled:opacity-70 hover:opacity-90 transition-opacity"
              >
                {loading ? 'Authenticating...' : 'Login to ETIMS'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;